import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { TestTrend, DateRange } from '../types/trends';

interface CachedTrendData {
  data: TestTrend;
  timestamp: number;
  dateRange: string;
  referenceSource: string;
}

interface TrendsDataContextType {
  trendsCache: Map<string, CachedTrendData>;
  getCachedTrend: (testName: string, dateRange: string, referenceSource: string) => TestTrend | null;
  setCachedTrend: (testName: string, data: TestTrend, dateRange: string, referenceSource: string) => void;
  clearCache: () => void;
}

const TrendsDataContext = createContext<TrendsDataContextType | null>(null);

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const TrendsDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use ref to persist cache across re-renders without causing them
  const cacheRef = useRef<Map<string, CachedTrendData>>(new Map());

  const getCachedTrend = useCallback((testName: string, dateRange: string, referenceSource: string): TestTrend | null => {
    const cacheKey = `${testName}_${dateRange}_${referenceSource}`;
    const cached = cacheRef.current.get(cacheKey);
    
    if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
      console.log(`✅ Cache hit for ${testName}`);
      return cached.data;
    }
    
    // Remove expired entry
    if (cached) {
      cacheRef.current.delete(cacheKey);
    }
    
    return null;
  }, []);

  const setCachedTrend = useCallback((testName: string, data: TestTrend, dateRange: string, referenceSource: string) => {
    const cacheKey = `${testName}_${dateRange}_${referenceSource}`;
    cacheRef.current.set(cacheKey, {
      data,
      timestamp: Date.now(),
      dateRange,
      referenceSource
    });
    console.log(`💾 Cached ${testName} (total cached: ${cacheRef.current.size})`);
  }, []);

  const clearCache = useCallback(() => {
    cacheRef.current.clear();
    console.log('🗑️ Cache cleared');
  }, []);

  const value = {
    trendsCache: cacheRef.current,
    getCachedTrend,
    setCachedTrend,
    clearCache
  };

  return (
    <TrendsDataContext.Provider value={value}>
      {children}
    </TrendsDataContext.Provider>
  );
};

export const useTrendsCache = () => {
  const context = useContext(TrendsDataContext);
  if (!context) {
    throw new Error('useTrendsCache must be used within TrendsDataProvider');
  }
  return context;
};